import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { eventService } from '../../services/eventService';

const AdminEvents = ({ user, addNotification }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingEvent, setViewingEvent] = useState(null);
    const [editingEvent, setEditingEvent] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [registrations, setRegistrations] = useState([]);
    const [loadingRegistrations, setLoadingRegistrations] = useState(false);
    const [imageError, setImageError] = useState(false);
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    
    const [stats, setStats] = useState({
        total_events: 0,
        active_events: 0,
        past_events: 0,
        total_registrations: 0,
        events_this_month: 0
    });
    
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        event_date: '',
        event_time: '',
        end_time: '',
        location: '',
        max_attendees: 100,
        event_type: 'club',
        is_featured: 0,
        status: 'active'
    });
    
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    
    const eventTypes = [
        { id: 'club', name: 'Club Section' },
        { id: 'corporate', name: 'Corporate/Company' },
        { id: 'private', name: 'Private Function' },
        { id: 'social', name: 'Social Gathering' },
        { id: 'sporting', name: 'Sporting Activity' },
        { id: 'religious', name: 'Religious Event' },
        { id: 'political', name: 'Political Event' }
    ];
    
    const loadEvents = async () => {
        setLoading(true);
        try {
            const response = await eventService.getEvents(false, 100);
            if (response.success) {
                // Sort events by id in descending order (latest first)
                const sortedEvents = [...response.events].sort((a, b) => {
                    // Sort by created_at if available, otherwise by id
                    if (a.created_at && b.created_at) {
                        return new Date(b.created_at) - new Date(a.created_at);
                    }
                    return b.id - a.id;
                });
                setEvents(sortedEvents);
                console.log('Loaded events (latest first):', sortedEvents);
            }
            
            const statsResponse = await eventService.getEventStats();
            if (statsResponse.success) {
                setStats(statsResponse.stats);
            }
        } catch (error) {
            console.error('Error loading events:', error);
            if (addNotification) addNotification('Failed to load events', 'error');
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        loadEvents();
    }, []);
    
    // Reset to first page when events change
    useEffect(() => {
        setCurrentPage(1);
    }, [events]);
    
    // Get current page events
    const indexOfLastEvent = currentPage * itemsPerPage;
    const indexOfFirstEvent = indexOfLastEvent - itemsPerPage;
    const currentEvents = events.slice(indexOfFirstEvent, indexOfLastEvent);
    const totalPages = Math.ceil(events.length / itemsPerPage);
    
    // Change page
    const paginate = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
            // Scroll to top of table
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };
    
    const loadEventRegistrations = async (eventId) => {
        setLoadingRegistrations(true);
        try {
            const response = await eventService.getEventRegistrations(eventId);
            if (response.success) {
                setRegistrations(response.registrations || []);
                console.log('Loaded registrations:', response.registrations);
            }
        } catch (error) {
            console.error('Error loading registrations:', error);
        } finally {
            setLoadingRegistrations(false);
        }
    };
    
    const handleViewEvent = async (event) => {
        console.log('Viewing event:', event);
        setViewingEvent(event);
        setImageError(false);
        await loadEventRegistrations(event.id);
        setShowViewModal(true);
    };
    
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        });
    };
    
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                if (addNotification) addNotification('Please select a valid image file (JPEG, PNG, GIF, WEBP)', 'error');
                return;
            }
            
            if (file.size > 5 * 1024 * 1024) {
                if (addNotification) addNotification('Image size should be less than 5MB', 'error');
                return;
            }
            
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };
    
    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            event_date: '',
            event_time: '',
            end_time: '',
            location: '',
            max_attendees: 100,
            event_type: 'club',
            is_featured: 0,
            status: 'active'
        });
        setImageFile(null);
        setImagePreview('');
        setEditingEvent(null);
    };
    
    const handleEdit = (event) => {
        setEditingEvent(event);
        setFormData({
            title: event.title || '',
            description: event.description || '',
            event_date: event.event_date || '',
            event_time: event.event_time || '',
            end_time: event.end_time || '',
            location: event.location || '',
            max_attendees: event.max_attendees || 100,
            event_type: event.event_type || 'club',
            is_featured: event.is_featured || 0,
            status: event.status || 'active'
        });
        if (event.image_url) {
            const imageUrl = event.image_url.startsWith('http') ? event.image_url : event.image_url;
            setImagePreview(imageUrl);
        }
        setShowModal(true);
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title.trim()) {
            if (addNotification) addNotification('Please enter an event title', 'error');
            return;
        }
        if (!formData.description.trim()) {
            if (addNotification) addNotification('Please enter an event description', 'error');
            return;
        }
        if (!formData.event_date) {
            if (addNotification) addNotification('Please select an event date', 'error');
            return;
        }
        if (!formData.location.trim()) {
            if (addNotification) addNotification('Please enter an event location', 'error');
            return;
        }
        
        setSubmitting(true);
        
        try {
            let response;
            if (editingEvent) {
                response = await eventService.updateEvent(editingEvent.id, formData, imageFile);
            } else {
                response = await eventService.createEvent(formData, imageFile);
            }
            
            if (response && response.success) {
                if (addNotification) addNotification(
                    editingEvent ? 'Event updated successfully!' : 'Event created successfully!',
                    'success'
                );
                resetForm();
                setShowModal(false);
                loadEvents();
            } else {
                if (addNotification) addNotification(
                    response?.message || 'Failed to save event',
                    'error'
                );
            }
        } catch (error) {
            console.error('Error saving event:', error);
            if (addNotification) addNotification(
                error.message || 'Error saving event. Please try again.',
                'error'
            );
        } finally {
            setSubmitting(false);
        }
    };
    
    const handleDelete = async () => {
        if (!showDeleteConfirm) return;
        
        setSubmitting(true);
        try {
            const response = await eventService.deleteEvent(showDeleteConfirm.id);
            if (response && response.success) {
                if (addNotification) addNotification('Event deleted successfully!', 'success');
                setShowDeleteConfirm(null);
                loadEvents();
            } else {
                if (addNotification) addNotification(
                    response?.message || 'Failed to delete event',
                    'error'
                );
            }
        } catch (error) {
            console.error('Error deleting event:', error);
            if (addNotification) addNotification('Error deleting event', 'error');
        } finally {
            setSubmitting(false);
        }
    };
    
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };
    
    const formatDateTime = (dateString, timeString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const formattedDate = date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
        if (timeString) {
            return `${formattedDate} at ${timeString.substring(0, 5)}`;
        }
        return formattedDate;
    };
    
    const formatTime = (timeString) => {
        if (!timeString) return 'TBA';
        return timeString.substring(0, 5);
    };
    
    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'active': return 'status-active';
            case 'cancelled': return 'status-cancelled';
            case 'completed': return 'status-completed';
            default: return 'status-pending';
        }
    };
    
    // Helper function to get full image URL
    const getFullImageUrl = (imageUrl) => {
        if (!imageUrl) return null;
        if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
            return imageUrl;
        }
        if (imageUrl.startsWith('/')) {
            return `https://nlngraclub.com${imageUrl}`;
        }
        return `https://nlngraclub.com/${imageUrl}`;
    };
    
    // Render pagination buttons
    const renderPagination = () => {
        if (totalPages <= 1) return null;
        
        const pageNumbers = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }
        
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }
        
        return (
            <div className="pagination-container">
                <div className="pagination-info">
                    Showing {indexOfFirstEvent + 1} - {Math.min(indexOfLastEvent, events.length)} of {events.length} events
                </div>
                <div className="pagination-controls">
                    <button 
                        onClick={() => paginate(1)} 
                        disabled={currentPage === 1}
                        className="pagination-btn"
                        title="First Page"
                    >
                        <i className="fas fa-angle-double-left"></i>
                    </button>
                    <button 
                        onClick={() => paginate(currentPage - 1)} 
                        disabled={currentPage === 1}
                        className="pagination-btn"
                        title="Previous Page"
                    >
                        <i className="fas fa-angle-left"></i>
                    </button>
                    
                    {startPage > 1 && (
                        <>
                            <button onClick={() => paginate(1)} className="pagination-btn">1</button>
                            {startPage > 2 && <span className="pagination-ellipsis">...</span>}
                        </>
                    )}
                    
                    {pageNumbers.map(number => (
                        <button
                            key={number}
                            onClick={() => paginate(number)}
                            className={`pagination-btn ${currentPage === number ? 'active' : ''}`}
                        >
                            {number}
                        </button>
                    ))}
                    
                    {endPage < totalPages && (
                        <>
                            {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
                            <button onClick={() => paginate(totalPages)} className="pagination-btn">{totalPages}</button>
                        </>
                    )}
                    
                    <button 
                        onClick={() => paginate(currentPage + 1)} 
                        disabled={currentPage === totalPages}
                        className="pagination-btn"
                        title="Next Page"
                    >
                        <i className="fas fa-angle-right"></i>
                    </button>
                    <button 
                        onClick={() => paginate(totalPages)} 
                        disabled={currentPage === totalPages}
                        className="pagination-btn"
                        title="Last Page"
                    >
                        <i className="fas fa-angle-double-right"></i>
                    </button>
                </div>
            </div>
        );
    };
    
    return (
        <div className="admin-events">
            {/* Stats Cards */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon">📅</div>
                    <div className="stat-value">{stats.total_events}</div>
                    <div className="stat-label">Total Events</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">🟢</div>
                    <div className="stat-value">{stats.active_events}</div>
                    <div className="stat-label">Active Events</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">📆</div>
                    <div className="stat-value">{stats.events_this_month}</div>
                    <div className="stat-label">This Month</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">👥</div>
                    <div className="stat-value">{stats.total_registrations}</div>
                    <div className="stat-label">Total Registrations</div>
                </div>
            </div>
            
            {/* Header */}
            <div className="section-header">
            
                <button 
                    className="btn-primary" 
                    onClick={() => { 
                        resetForm(); 
                        setShowModal(true); 
                    }}
                >
                    <i className="fa-solid fa-plus"></i> Create Event
                </button>
            </div>
            
           {/* Events Table */}
{events.length === 0 ? (
    <div className="empty-state">
        <i className="fa-solid fa-calendar-alt"></i>
        <p>No events found. Create your first event!</p>
    </div>
) : (
    <>
        <div className="events-table-container">
            <table className="events-table">
                <thead>
                    <tr>
                        <th>Image</th>
                        <th>Title</th>
                        <th>Date & Time</th>
                        <th>Location</th>
                        <th>Capacity</th>
                        <th>Registered</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {currentEvents.map(event => (
                        <tr key={event.id}>
                            <td className="event-image-cell">
                                {event.image_url ? (
                                    <img 
                                        src={getFullImageUrl(event.image_url)} 
                                        alt={event.title} 
                                        className="event-thumbnail"
                                        onError={(e) => {
                                            console.error('Image failed to load:', event.image_url);
                                            e.target.style.display = 'none';
                                            e.target.parentElement.innerHTML = '<div class="event-thumbnail-placeholder"><i class="fa-solid fa-calendar-alt"></i></div>';
                                        }}
                                    />
                                ) : (
                                    <div className="event-thumbnail-placeholder">
                                        <i className="fa-solid fa-calendar-alt"></i>
                                    </div>
                                )}
                            </td>
                            <td className="event-title-cell">
                                <strong>{event.title}</strong>
                                <small>{event.description?.substring(0, 50)}...</small>
                            </td>
                            <td>
                                <div>{formatDate(event.event_date)}</div>
                                <small>{formatTime(event.event_time)}</small>
                            </td>
                            <td>{event.location}</td>
                            <td>{event.max_attendees}</td>
                            <td>
                                <span className="registered-count">
                                    {event.registered_count || 0} / {event.max_attendees}
                                </span>
                            </td>
                            <td>
                                <span className={`status-badge ${getStatusBadgeClass(event.status)}`}>
                                    {event.status}
                                </span>
                            </td>
                            <td className="actions-cell">
                                <button 
                                    className="action-btn view" 
                                    onClick={() => handleViewEvent(event)}
                                    title="View Details"
                                >
                                    <i className="fas fa-eye"></i>
                                </button>
                                <button 
                                    className="action-btn edit" 
                                    onClick={() => handleEdit(event)}
                                    title="Edit"
                                >
                                    <i className="fas fa-edit"></i>
                                </button>
                                <button 
                                    className="action-btn delete" 
                                    onClick={() => setShowDeleteConfirm(event)}
                                    title="Delete"
                                >
                                    <i className="fas fa-trash"></i>
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        
        {/* Pagination */}
        {renderPagination()}
    </>
)}
            {/* View Event Modal */}
            <AnimatePresence>
                {showViewModal && viewingEvent && (
                    <motion.div 
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowViewModal(false)}
                    >
                        <motion.div 
                            className="modal-content view-modal"
                            initial={{ scale: 0.9, y: 30 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 30 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h3>Event Details</h3>
                                <button className="close-btn" onClick={() => setShowViewModal(false)}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            
                            <div className="modal-body">
                                {viewingEvent.image_url && (
                                    <div className="event-full-image">
                                        <img 
                                            src={getFullImageUrl(viewingEvent.image_url)} 
                                            alt={viewingEvent.title}
                                            onError={(e) => {
                                                console.error('Modal image failed to load:', viewingEvent.image_url);
                                                e.target.style.display = 'none';
                                                e.target.parentElement.innerHTML = '<div class="event-full-image-placeholder"><i class="fa-solid fa-image"></i><p>No image available</p></div>';
                                            }}
                                        />
                                    </div>
                                )}
                                
                                <h2 className="event-title">{viewingEvent.title}</h2>
                                
                                <div className="event-status-badge">
                                    <span className={`status-badge ${getStatusBadgeClass(viewingEvent.status)}`}>
                                        {viewingEvent.status.toUpperCase()}
                                    </span>
                                </div>
                                
                                <div className="event-info-grid">
                                    <div className="info-item">
                                        <i className="fas fa-calendar-alt"></i>
                                        <div>
                                            <strong>Date & Time</strong>
                                            <p>{formatDateTime(viewingEvent.event_date, viewingEvent.event_time)}</p>
                                        </div>
                                    </div>
                                    {viewingEvent.end_time && (
                                        <div className="info-item">
                                            <i className="fas fa-clock"></i>
                                            <div>
                                                <strong>End Time</strong>
                                                <p>{formatTime(viewingEvent.end_time)}</p>
                                            </div>
                                        </div>
                                    )}
                                    <div className="info-item">
                                        <i className="fas fa-map-marker-alt"></i>
                                        <div>
                                            <strong>Location</strong>
                                            <p>{viewingEvent.location}</p>
                                        </div>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-tag"></i>
                                        <div>
                                            <strong>Event Type</strong>
                                            <p>{eventTypes.find(t => t.id === viewingEvent.event_type)?.name || viewingEvent.event_type}</p>
                                        </div>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-users"></i>
                                        <div>
                                            <strong>Capacity</strong>
                                            <p>{viewingEvent.registered_count || 0} / {viewingEvent.max_attendees} registered</p>
                                        </div>
                                    </div>
                                    {viewingEvent.is_featured === 1 && (
                                        <div className="info-item">
                                            <i className="fas fa-star"></i>
                                            <div>
                                                <strong>Featured Event</strong>
                                                <p>This event is featured prominently</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="event-description">
                                    <h4>Description</h4>
                                    <p>{viewingEvent.description}</p>
                                </div>
                                
                                <div className="event-registrations">
                                    <h4>
                                        <i className="fas fa-users"></i>
                                        Registrations ({registrations.length})
                                    </h4>
                                    {loadingRegistrations ? (
                                        <div className="loading-small">
                                            <i className="fas fa-spinner fa-spin"></i> Loading registrations...
                                        </div>
                                    ) : registrations.length > 0 ? (
                                        <div className="registrations-list">
                                            <table className="registrations-table">
                                                <thead>
                                                    <tr>
                                                        <th>Member</th>
                                                        <th>Guests</th>
                                                        <th>Registered</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {registrations.map(reg => (
                                                        <tr key={reg.id}>
                                                            <td>
                                                                <strong>{reg.full_name || reg.first_name + ' ' + reg.last_name}</strong>
                                                                <small>{reg.email}</small>
                                                            </td>
                                                            <td>{reg.guests_count || 0}</td>
                                                            <td>{new Date(reg.registered_at).toLocaleDateString()}</td>
                                                            <td>
                                                                <span className={`status-badge status-${reg.registration_status}`}>
                                                                    {reg.registration_status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <p className="no-registrations">No registrations yet.</p>
                                    )}
                                </div>
                            </div>
                            
                            <div className="modal-footer">
                                <button 
                                    className="btn-secondary" 
                                    onClick={() => setShowViewModal(false)}
                                >
                                    Close
                                </button>
                                <button 
                                    className="btn-primary" 
                                    onClick={() => {
                                        setShowViewModal(false);
                                        handleEdit(viewingEvent);
                                    }}
                                >
                                    <i className="fas fa-edit"></i> Edit Event
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            
            {/* Create/Edit Modal */}
            <AnimatePresence>
                {showModal && (
                    <motion.div 
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowModal(false)}
                    >
                        <motion.div 
                            className="modal-content event-modal"
                            initial={{ scale: 0.9, y: 30 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 30 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h3>{editingEvent ? 'Edit Event' : 'Create New Event'}</h3>
                                <button className="close-btn" onClick={() => setShowModal(false)}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            
                            <form onSubmit={handleSubmit} className="event-form">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Event Title *</label>
                                        <input
                                            type="text"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleInputChange}
                                            placeholder="Enter event title"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Event Type</label>
                                        <select name="event_type" value={formData.event_type} onChange={handleInputChange}>
                                            {eventTypes.map(type => (
                                                <option key={type.id} value={type.id}>{type.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label>Description *</label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows="4"
                                        placeholder="Event description..."
                                        required
                                    ></textarea>
                                </div>
                                
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Event Date *</label>
                                        <input
                                            type="date"
                                            name="event_date"
                                            value={formData.event_date}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Start Time</label>
                                        <input
                                            type="time"
                                            name="event_time"
                                            value={formData.event_time}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>End Time</label>
                                        <input
                                            type="time"
                                            name="end_time"
                                            value={formData.end_time}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Location *</label>
                                        <input
                                            type="text"
                                            name="location"
                                            value={formData.location}
                                            onChange={handleInputChange}
                                            placeholder="Event location"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Max Attendees</label>
                                        <input
                                            type="number"
                                            name="max_attendees"
                                            value={formData.max_attendees}
                                            onChange={handleInputChange}
                                            min="1"
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Event Image</label>
                                        <input
                                            type="file"
                                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                            onChange={handleImageChange}
                                            className="file-input"
                                        />
                                        <small className="form-hint">Max 5MB. JPG, PNG, GIF, or WEBP</small>
                                        {imagePreview && (
                                            <div className="image-preview">
                                                <img src={imagePreview} alt="Preview" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label>Status</label>
                                        <select name="status" value={formData.status} onChange={handleInputChange}>
                                            <option value="active">Active</option>
                                            <option value="cancelled">Cancelled</option>
                                            <option value="completed">Completed</option>
                                        </select>
                                    </div>
                                </div>
                                
                                <div className="form-group checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="is_featured"
                                            checked={formData.is_featured === 1}
                                            onChange={handleInputChange}
                                        />
                                        <span>Feature this event (display prominently)</span>
                                    </label>
                                </div>
                                
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn-secondary" 
                                        onClick={() => setShowModal(false)}
                                        disabled={submitting}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn-primary" 
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <i className="fas fa-spinner fa-spin"></i> 
                                                {editingEvent ? 'Updating...' : 'Creating...'}
                                            </>
                                        ) : (
                                            editingEvent ? 'Update Event' : 'Create Event'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            
            {/* Delete Confirmation Modal */}
            <AnimatePresence>
                {showDeleteConfirm && (
                    <motion.div 
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowDeleteConfirm(null)}
                    >
                        <motion.div 
                            className="modal-content delete-modal"
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.9 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h3>Delete Event</h3>
                                <button className="close-btn" onClick={() => setShowDeleteConfirm(null)}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete "<strong>{showDeleteConfirm.title}</strong>"?</p>
                                <p className="text-warning">This action cannot be undone. All registrations will also be deleted.</p>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    className="btn-secondary" 
                                    onClick={() => setShowDeleteConfirm(null)}
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="btn-danger" 
                                    onClick={handleDelete}
                                    disabled={submitting}
                                >
                                    {submitting ? <i className="fas fa-spinner fa-spin"></i> : 'Delete Event'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminEvents;